import { useState } from 'react'
import { Clock, Droplets, Link2, Pencil, Video, VideoOff } from 'lucide-react'

import { IrrigationDeviceCamerasDialog } from './irrigation-device-cameras-dialog'
import { IrrigationPumpAction } from './irrigation-pump-action'
import { IrrigationPumpBadge } from './irrigation-pump-badge'
import { IrrigationRenameDialog } from './irrigation-rename-dialog'
import { IrrigationScheduleSheet } from './irrigation-schedule-sheet'
import { IrrigationSensorGrid } from './irrigation-sensor-grid'
import type { IrrigationDevice } from '@/lib/api/irrigation-devices-api'
import { CameraLiveDialog } from '@/components/cameras/camera-live-dialog'
import { CameraSwitchView } from '@/components/cameras/camera-switch-view'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useIrrigationDeviceCameras } from '@/hooks/use-cameras-query'
import { formatDateTime } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

/** Nút trên thẻ: 44px trên điện thoại (dễ bấm bằng tay), gọn lại trên máy tính. */
const CARD_BUTTON = 'h-11 md:h-9'
/** Nút phụ chia đều bề ngang hàng thứ hai. */
const SECONDARY = 'flex-1'

export interface DeviceListProps {
    devices: Array<IrrigationDevice>
    canControl: boolean
}

/**
 * Lưới thẻ trạm bơm, cùng kiểu màn Danh sách camera: mỗi thẻ có khung camera
 * gắn với trạm ở trên, thông tin và nút bật/tắt ở dưới — nhân viên nhìn thấy
 * trạm thật ngay chỗ bấm. Một cột trên điện thoại, 2–3 cột trên máy tính.
 */
export function DeviceGrid({ devices, canControl }: DeviceListProps) {
    return (
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {devices.map((device) => (
                <li key={device.id}>
                    <DeviceCard device={device} canControl={canControl} />
                </li>
            ))}
        </ul>
    )
}

function DeviceCard({
    device,
    canControl,
}: {
    device: IrrigationDevice
    canControl: boolean
}) {
    const { hasPermission } = useAuth()
    const canSeeCameras = hasPermission('cameras.read')
    const canManageCameras = hasPermission('cameras.write')
    const [camerasOpen, setCamerasOpen] = useState(false)
    const [runOpen, setRunOpen] = useState(false)
    const [renameOpen, setRenameOpen] = useState(false)

    const running = device.reading_state === 'ok' && device.pump_on === true

    return (
        <article
            className={twJoin(
                'flex h-full flex-col overflow-hidden rounded-lg border bg-card shadow-sm transition-colors duration-200',
                // Trạm đang bơm nổi bật để lướt qua là thấy — kèm chữ trong
                // nhãn trạng thái, không chỉ dựa vào màu.
                running && 'border-primary ring-1 ring-primary/40',
            )}
        >
            {canSeeCameras && (
                <StationCameras
                    device={device}
                    canControl={canControl}
                    canManage={canManageCameras}
                    // Hộp thoại quản lý camera đang mở thì thẻ ngừng lấy ảnh.
                    paused={camerasOpen || runOpen}
                    onManage={() => setCamerasOpen(true)}
                />
            )}
            <div className="flex flex-1 flex-col gap-3 p-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex items-start gap-1">
                            {/* Tên dài xuống tối đa 2 dòng thay vì cắt còn "…"
                                (tên trạm là thứ người vận hành đọc để chọn đúng). */}
                            <h2
                                className="line-clamp-2 min-w-0 break-words text-base font-semibold leading-snug"
                                title={device.name}
                            >
                                {device.name}
                            </h2>
                            {canControl && (
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    // Kéo lên ngang dòng tên; vùng bấm vẫn đủ 44px trên điện thoại.
                                    className="-my-2.5 shrink-0 text-muted-foreground md:-my-2"
                                    aria-label={m.irrigation_rename_action({
                                        name: device.name,
                                    })}
                                    title={m.irrigation_rename_action({
                                        name: device.name,
                                    })}
                                    onClick={() => setRenameOpen(true)}
                                >
                                    <Pencil className="size-4" />
                                </Button>
                            )}
                        </div>
                        <p className="truncate font-mono text-xs text-muted-foreground">
                            {device.id}
                        </p>
                    </div>
                    <IrrigationPumpBadge device={device} />
                </div>
                <dl className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2">
                        <dt className="flex shrink-0 items-center gap-1.5 text-muted-foreground">
                            <Droplets aria-hidden className="size-4" />
                            {m.irrigation_devices_col_valves()}
                        </dt>
                        <dd className="min-w-0 truncate">
                            <Valves valves={device.valves} />
                        </dd>
                    </div>
                    <div className="flex items-center gap-2">
                        <dt className="flex shrink-0 items-center gap-1.5 text-muted-foreground">
                            <Clock aria-hidden className="size-4" />
                            {m.irrigation_devices_col_received_at()}
                        </dt>
                        <dd className="min-w-0 truncate">
                            <ReceivedAt device={device} />
                        </dd>
                    </div>
                </dl>
                <IrrigationSensorGrid device={device} />
                {/* Hàng thao tác: Bật/Tắt đứng đầu (việc chính), lịch và camera
                    sau; cách nhau 8px để không bấm nhầm trên điện thoại. */}
                {/* Mọi màn hình cùng một bố cục: hàng 1 Bật/Tắt, hàng 2 Lịch
                    tưới + Camera, chia đều bề ngang. Nút Bật khi đếm ngược dài
                    ra, để chung một hàng với 3 nút kia là gãy dòng lộn xộn và
                    hàng nút các thẻ cạnh nhau lệch nhau. */}
                <div className="mt-auto flex flex-col gap-2 border-t pt-3">
                    {canControl && (
                        <IrrigationPumpAction
                            device={device}
                            onRunDialogChange={setRunOpen}
                            buttonClassName={CARD_BUTTON}
                            fillRow
                            countdownInButton
                        />
                    )}
                    <div className="flex gap-2">
                        {/* Ai xem được bộ tưới đều mở được lịch (chỉ xem); sửa
                            lịch cần quyền điều khiển. */}
                        <IrrigationScheduleSheet
                            device={device}
                            canEdit={canControl}
                            buttonClassName={twJoin(CARD_BUTTON, SECONDARY)}
                        />
                        {canSeeCameras && (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className={twJoin(CARD_BUTTON, SECONDARY)}
                                onClick={() => setCamerasOpen(true)}
                            >
                                <Video className="size-4" />
                                {m.camera_station_open()}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            {canControl && (
                <IrrigationRenameDialog
                    device={device}
                    open={renameOpen}
                    onOpenChange={setRenameOpen}
                />
            )}
            {canSeeCameras && (
                <IrrigationDeviceCamerasDialog
                    device={device}
                    open={camerasOpen}
                    onOpenChange={setCamerasOpen}
                    canManage={canManageCameras}
                />
            )}
        </article>
    )
}

/**
 * Khung camera của trạm trên thẻ: phát LUỒNG SỐNG (owner chốt 2026-09-28 —
 * chỉ khoảng 3 trạm nên băng thông chịu được). Mở xem trực tiếp, hộp thoại Bật
 * hay hộp thoại quản lý camera thì thẻ tạm ngắt luồng, để mỗi camera chỉ một
 * kết nối. Bấm vào khung mở xem trực tiếp, trong đó có luôn nút Bật/Tắt bơm.
 */
function StationCameras({
    device,
    canControl,
    canManage,
    paused,
    onManage,
}: {
    device: IrrigationDevice
    canControl: boolean
    canManage: boolean
    paused: boolean
    onManage: () => void
}) {
    const { hasPermission } = useAuth()
    const { data: cameras = [], isLoading } = useIrrigationDeviceCameras(
        device.id,
    )
    const [liveId, setLiveId] = useState<string | null>(null)
    const detailOpen = cameras.some((c) => c.id === liveId)

    if (isLoading) {
        // Giữ đúng khung 16:9 lúc đang tải để thẻ không nhảy chiều cao.
        return (
            <div className="flex aspect-video items-center justify-center bg-muted text-sm text-muted-foreground motion-safe:animate-pulse">
                {m.common_loading()}
            </div>
        )
    }
    if (cameras.length === 0) {
        return (
            <div className="flex aspect-video flex-col items-center justify-center gap-2 bg-muted/60 text-center">
                <VideoOff className="size-6 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                    {m.camera_station_empty()}
                </p>
                {canManage && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className={CARD_BUTTON}
                        onClick={onManage}
                    >
                        <Link2 className="size-4" />
                        {m.camera_station_attach()}
                    </Button>
                )}
            </div>
        )
    }

    return (
        <>
            <CameraSwitchView
                cameras={cameras}
                mode="live"
                active={!detailOpen && !paused}
                className="aspect-video w-full"
                onOpen={(camera) => setLiveId(camera.id)}
            />
            <CameraLiveDialog
                cameras={cameras}
                cameraId={liveId}
                onCameraChange={setLiveId}
                canControl={hasPermission('cameras.control')}
                describe={(camera) => `${device.name} · ${camera.name}`}
                extra={
                    canControl ? (
                        <div className="space-y-2 pt-2">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                    {m.camera_station_pump_title()}
                                </span>
                                <IrrigationPumpBadge device={device} />
                            </div>
                            <IrrigationPumpAction
                                device={device}
                                showCamera={false}
                            />
                        </div>
                    ) : undefined
                }
            />
        </>
    )
}

function Valves({ valves }: { valves: Array<string> }) {
    if (valves.length === 0) {
        return (
            <span className="text-muted-foreground">
                {m.irrigation_devices_no_valves()}
            </span>
        )
    }
    return <span>{valves.join(', ')}</span>
}

function ReceivedAt({ device }: { device: IrrigationDevice }) {
    return device.received_at ? (
        <span className="tabular-nums">
            {formatDateTime(device.received_at)}
        </span>
    ) : (
        <span className="text-muted-foreground">—</span>
    )
}
